import {
  createLabWorkTypeSchema,
  type CreateLabWorkTypeInput,
  type LabWorkType,
} from "@clinic/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, type JSX } from "react";
import { Controller, useForm } from "react-hook-form";
import { revealFirstError } from "@web/shared/lib/form-errors";
import { useTranslation } from "react-i18next";
import { Button, FormField, Input, Modal, MoneyInput, Switch, useToast } from "@clinic/ui";
import { useCreateWorkType, useUpdateWorkType } from "@web/modules/labs/queries";
import { errorToast } from "@web/shared/lib/api-error";
import { useCurrency } from "@web/shared/queries/clinic";

export function WorkTypeModal({
  open,
  onOpenChange,
  labId,
  workType,
  "data-testid": testId = "work-type-modal",
}: {
  readonly "data-testid"?: string | undefined;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly labId: string;
  readonly workType?: LabWorkType | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const currency = useCurrency();
  const toast = useToast();
  const create = useCreateWorkType();
  const update = useUpdateWorkType();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<CreateLabWorkTypeInput>({
    mode: "onTouched",
    resolver: zodResolver(createLabWorkTypeSchema),
    defaultValues: { name: "", defaultPrice: "", isActive: true },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: workType?.name ?? "",
        defaultPrice: workType?.defaultPrice ?? "",
        isActive: workType?.isActive ?? true,
      });
    }
  }, [open, workType, reset]);

  const submit = handleSubmit(
    async (values) => {
      try {
        if (workType) {
          await update.mutateAsync({ id: workType.id, body: values });
        } else {
          await create.mutateAsync({ labId, body: values });
        }

        toast.success("labs.prices.saved");
        onOpenChange(false);
      } catch (error) {
        toast.error(...errorToast(error));
      }
    },
    () => revealFirstError(),
  );

  return (
    <Modal
      data-testid={testId}
      open={open}
      onOpenChange={onOpenChange}
      title={t(workType ? "labs.prices.editTitle" : "labs.prices.addTitle")}
      footer={
        <>
          <Button
            variant="secondary"
            data-testid={`${testId}-cancel`}
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            isLoading={isSubmitting}
            aria-disabled={!isValid || isSubmitting || undefined}
            data-testid={`${testId}-save`}
            onClick={() => void submit()}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form
        data-testid={`${testId}-form`}
        className="flex flex-col gap-4"
        onSubmit={(event) => void submit(event)}
      >
        <FormField label="labs.prices.name" htmlFor="work-type-name" error={errors.name}>
          <Input
            id="work-type-name"
            data-testid="work-type-field-name"
            dir="ltr"
            placeholder="Zirconia crown"
            {...register("name")}
          />
        </FormField>

        <FormField
          label="labs.prices.price"
          htmlFor="work-type-price"
          error={errors.defaultPrice}
          hint={t("labs.prices.snapshotNote")}
        >
          <MoneyInput
            id="work-type-price"
            data-testid="work-type-field-price"
            currency={currency}
            placeholder="0"
            hasError={Boolean(errors.defaultPrice)}
            {...register("defaultPrice")}
          />
        </FormField>

        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <Switch
              checked={field.value ?? true}
              onCheckedChange={field.onChange}
              label={t("labs.prices.active")}
            />
          )}
        />
      </form>
    </Modal>
  );
}
